/**
 * SalesPriceStrip — the salesperson-facing pricing primitive.
 *
 * Leads with **ocean all-in per container** (FRT++) and places **locals** and
 * **per-B/L** charges beside it as quote add-ons rather than folding them into
 * the headline number. Pair it with `SalesPriceTotal` for the side total.
 *
 * Density variants:
 * - `hero`    — booking header (large ocean all-in, quieter add-ons)
 * - `row`     — lookup / table cells (single line + compact add-ons)
 * - `compare` — current vs alternative sides
 * - `rail`    — assigned-rate / surcharge panel
 */
import type { ReactNode } from "react";

import { CarrierContractLabel } from "@/components/common/CarrierContractLabel";
import { SensitiveMoney } from "@/components/common/Sensitive";
import { SurchargeCode } from "@/components/common/SurchargeCode";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import {
  salesPriceBreakdown,
  resolveHeroBlDisplay,
  perContainerSellPrice,
  type SalesPriceBreakdown,
  type SalesPriceInput,
  type SalesSurchargeChip,
} from "@/lib/salesPrice";

/** Hero headline metric on booking detail — ops vs sales (quote tier). */
export type SalesHeroPriceMetric = "ocean-all-in" | "per-container";

export type SalesPriceDensity = "hero" | "row" | "compare" | "rail";

interface SalesPriceStripProps {
  /** Raw line input; the breakdown is computed internally. */
  line?: SalesPriceInput;
  /** Pre-computed breakdown (takes precedence over `line`). */
  breakdown?: SalesPriceBreakdown;
  /** Equipment label shown above the hero (e.g. "40' HC × 2"). */
  equipmentLabel?: string;
  density?: SalesPriceDensity;
  /** Hide the monetary figures (quote-visibility current side). */
  hidePrice?: boolean;
  className?: string;
}

const HERO_PRICE_CLASS: Record<SalesPriceDensity, string> = {
  hero: "text-2xl font-semibold",
  row: "text-sm font-semibold",
  compare: "text-lg font-semibold",
  rail: "text-base font-semibold",
};

function MaskedDash() {
  return <span className="text-muted-foreground">•••</span>;
}

/** Inline list of surcharge chips: `THC 150 · THD 180 · DOC 85`. */
function ChipList({
  chips,
  hidePrice,
  className,
}: {
  chips: SalesSurchargeChip[];
  hidePrice?: boolean;
  className?: string;
}) {
  if (chips.length === 0) return null;
  return (
    <span
      className={cn(
        "flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] text-muted-foreground",
        className,
      )}
    >
      {chips.map((chip, i) => (
        <span key={`${chip.code}-${i}`} className="inline-flex items-center gap-1">
          {i > 0 && <span aria-hidden className="opacity-40">·</span>}
          <SurchargeCode code={chip.code} />
          {hidePrice ? (
            <MaskedDash />
          ) : (
            <SensitiveMoney amount={chip.amount} className="tabular-nums" />
          )}
        </span>
      ))}
    </span>
  );
}

/** The big ocean all-in number with its `/ ctr` unit and subtitle. */
function OceanAllIn({
  amount,
  density,
  hidePrice,
}: {
  amount: number;
  density: SalesPriceDensity;
  hidePrice?: boolean;
}) {
  return (
    <div className="flex flex-col leading-tight">
      <span className="inline-flex items-baseline gap-1">
        <span className={cn("tabular-nums", HERO_PRICE_CLASS[density])}>
          {hidePrice ? <MaskedDash /> : <SensitiveMoney amount={amount} />}
        </span>
        <span className="text-xs font-normal text-muted-foreground">/ ctr</span>
      </span>
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
        ocean all-in
      </span>
    </div>
  );
}

/** The per-B/L summary ($X / BL + subtitle). */
function PerBl({
  amount,
  hidePrice,
}: {
  amount: number;
  hidePrice?: boolean;
}) {
  if (amount <= 0) return null;
  return (
    <div className="flex flex-col leading-tight">
      <span className="inline-flex items-baseline gap-1">
        <span className="text-sm font-medium tabular-nums">
          {hidePrice ? <MaskedDash /> : <SensitiveMoney amount={amount} />}
        </span>
        <span className="text-xs font-normal text-muted-foreground">/ BL</span>
      </span>
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
        per BL
      </span>
    </div>
  );
}

export function SalesPriceStrip({
  line,
  breakdown,
  equipmentLabel,
  density = "row",
  hidePrice,
  className,
}: SalesPriceStripProps) {
  const data: SalesPriceBreakdown | null =
    breakdown ?? (line ? salesPriceBreakdown(line) : null);
  if (!data) return null;

  const addOnChips: SalesSurchargeChip[] = [...data.localChips, ...data.blChips];
  const blDisplay = data.blForTotal;

  // Compact layout for lookup rows: ocean all-in beside locals / per-BL.
  if (density === "row") {
    return (
      <div
        className={cn(
          "flex flex-wrap items-baseline justify-end gap-x-2 gap-y-0.5 leading-tight",
          className,
        )}
      >
        <span className="inline-flex shrink-0 items-baseline gap-1">
          <span className="text-sm font-semibold tabular-nums">
            {hidePrice ? <MaskedDash /> : <SensitiveMoney amount={data.oceanAllIn} />}
          </span>
          <span className="text-[10px] text-muted-foreground">/ ctr</span>
        </span>
        {(blDisplay > 0 || addOnChips.length > 0) && (
          <span className="inline-flex flex-wrap items-baseline gap-x-1.5 text-[11px] text-muted-foreground">
            {blDisplay > 0 && (
              <span className="inline-flex items-baseline gap-1">
                {hidePrice ? <MaskedDash /> : <SensitiveMoney amount={blDisplay} />}
                <span>/ BL</span>
              </span>
            )}
            {data.localChips.length > 0 && (
              <ChipList chips={data.localChips} hidePrice={hidePrice} />
            )}
          </span>
        )}
      </div>
    );
  }

  // Block layout: hero + per-BL + component chips side by side.
  if (density === "hero") {
    return (
      <SalesPriceHeroLineRow
        equipmentLabel={equipmentLabel}
        breakdown={data}
        hidePrice={hidePrice}
        className={className}
      />
    );
  }

  return (
    <div className={cn("flex flex-col gap-1", className)}>
      {equipmentLabel && (
        <span className="text-xs font-medium text-foreground">{equipmentLabel}</span>
      )}
      <div className="flex flex-wrap items-start gap-x-6 gap-y-2">
        <OceanAllIn amount={data.oceanAllIn} density={density} hidePrice={hidePrice} />
        <PerBl amount={blDisplay} hidePrice={hidePrice} />
        {addOnChips.length > 0 && (
          <div className="flex flex-col gap-0.5 leading-tight">
            <ChipList chips={addOnChips} hidePrice={hidePrice} />
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              locals + BL components
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

/** Same label/value rhythm as booking hero stats (no nested surface). */
function SalesPriceMetric({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("min-w-0 flex-1", className)}>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <div className="mt-1 min-w-0">{children}</div>
    </div>
  );
}

function SalesPriceHeroLineRow({
  equipmentLabel,
  breakdown,
  hidePrice,
  showEquipmentLabel = false,
  compactSplit = false,
  hideLinePerBl = false,
  heroMetric = "ocean-all-in",
  carrier,
  contractRef,
  namedAccount,
  rateCardId,
  className,
}: {
  equipmentLabel?: string;
  breakdown: SalesPriceBreakdown;
  hidePrice?: boolean;
  showEquipmentLabel?: boolean;
  /** Multi-contract split: carrier + contract header, tighter metrics. */
  compactSplit?: boolean;
  hideLinePerBl?: boolean;
  heroMetric?: SalesHeroPriceMetric;
  carrier?: string | null;
  contractRef?: string | null;
  namedAccount?: string | null;
  rateCardId?: string | null;
  className?: string;
}) {
  const heroLabel =
    heroMetric === "per-container" ? "Per container" : "Ocean all-in";
  const heroAmount =
    heroMetric === "per-container"
      ? perContainerSellPrice(breakdown)
      : breakdown.oceanAllIn;
  const blDisplay = hideLinePerBl
    ? {
        showPerBl: false,
        perBlAmount: 0,
        showBlComponents: false,
        blChips: [],
      }
    : resolveHeroBlDisplay(breakdown);
  const hasSplitHeader = Boolean(
    compactSplit && (carrier?.trim() || contractRef?.trim()),
  );
  const secondaryMetrics = [
    blDisplay.showPerBl && (
      <SalesPriceMetric key="bl-rate" label="Per BL">
        <span className="inline-flex items-baseline gap-1 tabular-nums">
          <span className="text-sm font-semibold">
            {hidePrice ? (
              <MaskedDash />
            ) : (
              <SensitiveMoney amount={blDisplay.perBlAmount} />
            )}
          </span>
          <span className="text-xs font-normal text-muted-foreground">/ BL</span>
        </span>
      </SalesPriceMetric>
    ),
    breakdown.localChips.length > 0 && (
      <SalesPriceMetric key="locals" label="Locals">
        <ChipList
          chips={breakdown.localChips}
          hidePrice={hidePrice}
          className="text-sm"
        />
      </SalesPriceMetric>
    ),
    blDisplay.showBlComponents && (
      <SalesPriceMetric key="bl-comp" label="BL components">
        <ChipList
          chips={blDisplay.blChips}
          hidePrice={hidePrice}
          className="text-sm"
        />
      </SalesPriceMetric>
    ),
  ].filter(Boolean);

  if (hasSplitHeader) {
    return (
      <div
        className={cn(
          "flex flex-col gap-2 border-b border-border/35 pb-3 last:border-b-0 last:pb-0",
          className,
        )}
      >
        <div className="flex flex-col gap-0.5">
          <CarrierContractLabel
            carrier={carrier}
            contractRef={contractRef}
            namedAccount={namedAccount}
            rateCardId={rateCardId}
            size={14}
            bold
            className="min-w-0 max-w-full flex-wrap"
            contractClassName="text-xs"
          />
          {equipmentLabel ? (
            <p className="text-xs font-medium text-muted-foreground">
              {equipmentLabel}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-end gap-x-5 gap-y-2">
          <SalesPriceMetric label={heroLabel} className="shrink-0">
            <span className="inline-flex items-baseline gap-1 tabular-nums">
              <span className="text-lg font-semibold">
                {hidePrice ? (
                  <MaskedDash />
                ) : (
                  <SensitiveMoney amount={heroAmount} />
                )}
              </span>
              <span className="text-xs font-normal text-muted-foreground">
                / ctr
              </span>
            </span>
          </SalesPriceMetric>
          {secondaryMetrics.length > 0 ? (
            <div className="flex min-w-0 flex-1 flex-wrap gap-x-4 gap-y-2">
              {secondaryMetrics}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {showEquipmentLabel && equipmentLabel ? (
        <HeroStat label="Equipment">{equipmentLabel}</HeroStat>
      ) : null}
      <SalesPriceMetric label={heroLabel}>
        <span className="inline-flex items-baseline gap-1 tabular-nums">
          <span className="text-xl font-semibold">
            {hidePrice ? (
              <MaskedDash />
            ) : (
              <SensitiveMoney amount={heroAmount} />
            )}
          </span>
          <span className="text-xs font-normal text-muted-foreground">/ ctr</span>
        </span>
      </SalesPriceMetric>
      {secondaryMetrics.length > 0 ? (
        <div className="grid grid-cols-1 gap-3 border-t border-border/35 pt-3 sm:grid-cols-2">
          {secondaryMetrics}
        </div>
      ) : null}
    </div>
  );
}

/** Matches booking detail hero stat labels (shared rhythm with shipment facts). */
function HeroStat({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("min-w-0", className)}>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <div className="mt-1 min-w-0 text-sm font-medium">{children}</div>
    </div>
  );
}

function SalesPriceHeroSkeleton() {
  return (
    <div
      className="flex flex-col gap-3 animate-pulse"
      aria-busy="true"
      aria-label="Loading pricing breakdown"
    >
      <div className="space-y-2">
        <div className="h-3 w-24 rounded-sm bg-muted/40" />
        <div className="h-8 w-32 rounded-sm bg-muted/50" />
      </div>
      <div className="grid grid-cols-2 gap-3 border-t border-border/25 pt-3">
        <div className="space-y-2">
          <div className="h-3 w-14 rounded-sm bg-muted/40" />
          <div className="h-5 w-20 rounded-sm bg-muted/50" />
        </div>
        <div className="space-y-2">
          <div className="h-3 w-12 rounded-sm bg-muted/40" />
          <div className="h-5 w-24 rounded-sm bg-muted/50" />
        </div>
      </div>
    </div>
  );
}

export interface SalesPriceHeroLine {
  key: string;
  equipmentLabel?: string;
  breakdown: SalesPriceBreakdown;
  carrier?: string | null;
  contractRef?: string | null;
  namedAccount?: string | null;
  rateCardId?: string | null;
}

interface SalesPriceHeroPanelProps {
  /** True while `/booking pricing` lines are loading. */
  loading?: boolean;
  lines?: SalesPriceHeroLine[];
  total?: number | null;
  recipe?: ReactNode;
  /** Booking-level all-in when line breakdown is unavailable. */
  fallbackPrice?: number | null;
  /** When several equipment lines exist, label each breakdown block. */
  showEquipmentLabel?: boolean;
  /** Multi-carrier/contract split — compact rows keyed by carrier + contract. */
  compactSplits?: boolean;
  /** Shown above split pricing (e.g. shared equipment on a split booking). */
  splitSectionCaption?: ReactNode;
  splitEquipmentLabel?: string;
  /**
   * Hide per-line Per BL / BL components on hero rows — use when BL is already
   * in the shipment total recipe (avoids looking charged once per equipment row).
   */
  hideLinePerBl?: boolean;
  /** Hero grid: breakdown column, total column, or both (default). */
  part?: "full" | "breakdown" | "total";
  heroMetric?: SalesHeroPriceMetric;
  hidePrice?: boolean;
  className?: string;
}

/** Booking header pricing card — breakdown metrics + shipment total. */
export function SalesPriceHeroPanel({
  loading = false,
  lines = [],
  total,
  recipe,
  fallbackPrice,
  showEquipmentLabel = false,
  compactSplits = false,
  splitSectionCaption,
  splitEquipmentLabel,
  hideLinePerBl = false,
  part = "full",
  heroMetric = "ocean-all-in",
  hidePrice,
  className,
}: SalesPriceHeroPanelProps) {
  const hasLines = lines.length > 0;
  const showTotal = total != null;
  const showFallback =
    !loading && !hasLines && fallbackPrice != null && fallbackPrice > 0;
  const showBreakdownPart = part === "full" || part === "breakdown";
  const showTotalPart = part === "full" || part === "total";

  const breakdownSection =
    loading && !hasLines ? (
      <SalesPriceHeroSkeleton />
    ) : hasLines ? (
      <div className="flex flex-col gap-2">
        {compactSplits && (splitSectionCaption || splitEquipmentLabel) ? (
          <div className="space-y-1 pb-1">
            {splitSectionCaption ? (
              <p className="text-[11px] leading-snug text-muted-foreground">
                {splitSectionCaption}
              </p>
            ) : null}
            {splitEquipmentLabel ? (
              <p className="text-xs font-medium text-foreground">
                {splitEquipmentLabel}
              </p>
            ) : null}
          </div>
        ) : null}
        <div className={cn(compactSplits ? "flex flex-col gap-0" : "flex flex-col gap-5")}>
          {lines.map((entry) => (
            <SalesPriceHeroLineRow
              key={entry.key}
              equipmentLabel={entry.equipmentLabel}
              breakdown={entry.breakdown}
              showEquipmentLabel={showEquipmentLabel}
              compactSplit={compactSplits}
              hideLinePerBl={hideLinePerBl}
              heroMetric={heroMetric}
              carrier={entry.carrier}
              contractRef={entry.contractRef}
              namedAccount={entry.namedAccount}
              rateCardId={entry.rateCardId}
              hidePrice={hidePrice}
            />
          ))}
        </div>
      </div>
    ) : showFallback ? (
      <SalesPriceMetric
        label={heroMetric === "per-container" ? "Per container" : "All-in price"}
      >
        <span className="text-xl font-semibold tabular-nums">
          {hidePrice ? (
            <MaskedDash />
          ) : (
            <SensitiveMoney amount={fallbackPrice} />
          )}
        </span>
      </SalesPriceMetric>
    ) : null;

  const hasBreakdownContent =
    loading || hasLines || (showBreakdownPart && showFallback);
  const hasTotalContent = showTotal || (showTotalPart && showFallback);

  if (part === "breakdown" && !hasBreakdownContent) {
    return null;
  }
  if (part === "total" && !hasTotalContent) {
    return null;
  }
  if (
    part === "full" &&
    !loading &&
    !hasLines &&
    !showTotal &&
    !showFallback
  ) {
    return null;
  }

  const showDivider =
    part === "full" && Boolean(breakdownSection) && showTotal;

  if (part === "breakdown") {
    return (
      <div className={cn("w-full min-w-0", className)}>{breakdownSection}</div>
    );
  }

  if (part === "total") {
    if (!showTotal) return null;
    return (
      <div className={cn("w-full min-w-0", className)}>
        <SalesPriceTotal
          total={total}
          recipe={loading && !hasLines ? undefined : recipe}
          hidePrice={hidePrice}
          className="items-start text-left sm:items-end sm:text-right"
        />
      </div>
    );
  }

  return (
    <div className={cn("w-full min-w-0", className)}>
      <div className="flex flex-col gap-4 sm:items-end lg:flex-row lg:items-start lg:gap-10">
        {breakdownSection ? (
          <div className="min-w-0 w-full sm:max-w-xs sm:flex-1">
            {breakdownSection}
          </div>
        ) : null}
        {showTotal ? (
          <>
            {showDivider ? (
              <>
                <Separator className="lg:hidden" />
                <div
                  className="hidden w-px shrink-0 self-stretch bg-border/40 lg:block"
                  aria-hidden
                />
              </>
            ) : null}
            <SalesPriceTotal
              total={total}
              recipe={loading && !hasLines ? undefined : recipe}
              hidePrice={hidePrice}
              className="shrink-0 items-start text-left sm:items-end sm:text-right"
            />
          </>
        ) : null}
      </div>
    </div>
  );
}

interface SalesPriceTotalProps {
  total: number;
  /** One-line recipe such as `2 × $1,620 + locals + $85 BL`. */
  recipe?: ReactNode;
  label?: string;
  hidePrice?: boolean;
  className?: string;
}

/** Side total block: prominent shipment total + a plain-language recipe. */
export function SalesPriceTotal({
  total,
  recipe,
  label = "Shipment total",
  hidePrice,
  className,
}: SalesPriceTotalProps) {
  return (
    <div className={cn("flex flex-col items-end gap-0.5 text-right", className)}>
      <span className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="text-2xl font-semibold tabular-nums">
        {hidePrice ? <MaskedDash /> : <SensitiveMoney amount={total} />}
      </span>
      {recipe && (
        <span className="text-[11px] text-muted-foreground">{recipe}</span>
      )}
    </div>
  );
}

/**
 * Build a plain-language recipe string for a single-line total, e.g.
 * `2 × $1,620 + locals + $85 BL`. Amounts are intentionally rounded for a
 * glanceable summary; the authoritative figure is the total itself.
 */
export function buildSalesRecipe(breakdown: SalesPriceBreakdown): string {
  const parts: string[] = [];
  const perCtr = breakdown.oceanAllIn + breakdown.localsPerContainer;
  const ctr = `$${Math.round(perCtr).toLocaleString("en-US")}`;
  parts.push(breakdown.quantity > 1 ? `${breakdown.quantity} × ${ctr}` : ctr);
  if (breakdown.blForTotal > 0) {
    parts.push(`$${Math.round(breakdown.blForTotal).toLocaleString("en-US")} BL`);
  }
  return parts.join(" + ");
}
